import { HTTPError } from 'ky';
import { getApiClient } from './client';
import type {
  Container,
  ContainerKind,
  FileMailRequest,
  FileMailResponse,
  Folder,
  MailSummary,
  SearchResponse,
} from '@/types/domain';

export async function listContainers(): Promise<Container[]> {
  return getApiClient().get('outlook-list-containers').json<Container[]>();
}

export async function listFavorites(): Promise<Container[]> {
  return getApiClient().get('outlook-list-favorites').json<Container[]>();
}

export async function toggleFavorite(input: {
  containerId: string;
  favorite: boolean;
}): Promise<{ favorited: boolean }> {
  return getApiClient()
    .post('outlook-toggle-favorite', { json: input })
    .json<{ favorited: boolean }>();
}

export async function listFolders(containerId: string): Promise<Folder[]> {
  return getApiClient()
    .get('outlook-list-folders', { searchParams: { container_id: containerId } })
    .json<Folder[]>();
}

export async function fileMail(payload: FileMailRequest): Promise<FileMailResponse> {
  try {
    // Filing an attachment-heavy mail (base64 upload + server-side eml→msg
    // conversion + S3 upload) routinely takes far longer than the default 30s.
    // A too-short timeout made the client give up while the server kept going
    // and committed the row — the follow-up attempt then hit the dedup guard
    // (HTTP 409). Give this call a generous ceiling so it succeeds on the first
    // try. (Fast, attachment-free mails still return immediately.)
    return await getApiClient()
      .post('outlook-file-mail', { json: payload, timeout: 120_000 })
      .json<FileMailResponse>();
  } catch (err) {
    // A duplicate is an EXPECTED outcome, not a failure: the mail is already
    // filed at that destination. The server signals it with HTTP 409 +
    // { status: 'duplicate', existing }. ky throws on any non-2xx, so without
    // this the duplicate surfaced to the user as the raw "Request failed with
    // status code 409". Unwrap the body and return it as data so the callers'
    // existing duplicate handling (friendly "déjà rangé" toast) runs instead.
    if (err instanceof HTTPError && err.response.status === 409) {
      // Read the body from `err.data`, NOT `err.response.json()`: ky 2 already
      // consumed the stream to pre-parse it, so re-reading the response throws
      // "body stream already read". That throw escaped this handler and reached
      // the user as a cryptic stream error in place of the "déjà rangé" toast.
      const data = err.data as FileMailResponse | string | undefined;
      // A JSON content-type gives the parsed body; anything else (an empty body,
      // or a proxy that swallowed it) would leave a string/undefined here, and
      // returning that would fail the `status === 'duplicate'` check silently.
      // Fall back to a well-formed duplicate: HTTP 409 from this endpoint means
      // duplicate, even when the body didn't survive.
      if (data && typeof data === 'object' && data.status === 'duplicate') {
        return data;
      }
      return { status: 'duplicate' };
    }
    throw err;
  }
}

export async function listMails(folderId: string): Promise<MailSummary[]> {
  return getApiClient()
    .get('outlook-list-mails', { searchParams: { folder_id: folderId } })
    .json<MailSummary[]>();
}

// Mails filed directly at a project root (folder_id null).
export async function listRootMails(containerId: string): Promise<MailSummary[]> {
  return getApiClient()
    .get('outlook-list-mails', { searchParams: { container_id: containerId } })
    .json<MailSummary[]>();
}

// Streams the stored .msg back through the edge function (Leviia rejects
// presigned URLs). The ky client attaches the bearer token; we save the blob.
export async function downloadMail(mailId: string): Promise<Blob> {
  // Large (attachment-heavy) .msg files can take a while to stream back; allow
  // the same generous ceiling as filing so the download doesn't abort at 30s.
  return getApiClient().post('outlook-download-mail', { json: { mailId }, timeout: 120_000 }).blob();
}

/**
 * Pose ou retire l'état « lu par l'appelant » sur un mail rangé.
 *
 * Idempotent dans les deux sens côté serveur (`on conflict do nothing` pour
 * `true`, suppression de la ligne pour `false`), donc rappelable sans précaution.
 */
export async function setMailRead(mailId: string, read: boolean): Promise<void> {
  await getApiClient().post('outlook-mark-mail-read', { json: { mailId, read } });
}

export async function moveMail(input: { mailId: string; targetFolderId: string }): Promise<void> {
  await getApiClient().post('outlook-move-mail', { json: input });
}

export async function deleteMail(mailId: string): Promise<void> {
  await getApiClient().post('outlook-delete-mail', { json: { mailId } });
}

export async function copyMail(input: {
  mailId: string;
  targetFolderId: string;
}): Promise<{ status: 'created'; mailId: string }> {
  return getApiClient()
    .post('outlook-copy-mail', { json: input })
    .json<{ status: 'created'; mailId: string }>();
}

export async function renameMail(input: { mailId: string; displayName: string }): Promise<void> {
  await getApiClient().post('outlook-rename-mail', { json: input });
}

export async function resolveUsers(oids: string[]): Promise<Record<string, string>> {
  if (oids.length === 0) return {};
  return getApiClient()
    .post('outlook-resolve-users', { json: { oids } })
    .json<Record<string, string>>();
}

export async function search(q: string): Promise<SearchResponse> {
  return getApiClient().post('outlook-search', { json: { q } }).json<SearchResponse>();
}

export async function createFolder(input: {
  containerId: string;
  parentId: string | null;
  name: string;
}): Promise<Folder> {
  return getApiClient().post('outlook-create-folder', { json: input }).json<Folder>();
}

export async function moveFolder(input: {
  folderId: string;
  newParentId: string | null;
}): Promise<Folder> {
  return getApiClient().post('outlook-move-folder', { json: input }).json<Folder>();
}

export async function renameFolder(input: { folderId: string; name: string }): Promise<Folder> {
  return getApiClient().post('outlook-rename-folder', { json: input }).json<Folder>();
}

export async function deleteFolder(folderId: string): Promise<void> {
  await getApiClient().post('outlook-delete-folder', { json: { folderId } });
}

export interface Whoami {
  oid: string;
  upn: string | null;
  isAdmin: boolean;
}

export interface AdminUser {
  oid: string;
  upn: string | null;
  isAdmin: boolean;
  refreshedAt: string;
}

export interface MissingGroupProject {
  id: string;
  kind: 'project';
  name: string;
  number: string;
  status: string;
  prism_id: string;
  /**
   * 0029 : les projets ARCHIVÉS sans groupe sont désormais listés eux aussi.
   * Avant, ils étaient exclus du rapport — donc invisibles pour l'utilisateur,
   * jamais liés par le cron d'auto-liaison, et absents de tout écran : personne
   * ne pouvait les récupérer. Un archivé reste un écart de priorité moindre, pas
   * un écart absent : le tableau le signale, le serveur le trie après les actifs.
   */
  archived: boolean;
}

export async function whoami(): Promise<Whoami> {
  return getApiClient().get('outlook-whoami').json<Whoami>();
}

/**
 * Préférences de l'utilisateur courant (cf. migrations 0025 et 0028).
 *
 * Toutes agissent sur la boîte mail Outlook — dont la suppression du message
 * d'origine — donc toutes sont FAUSSES par défaut, et le serveur répond ces mêmes
 * défauts quand l'utilisateur n'a jamais rien réglé.
 */
export interface UserPreferences {
  /** Déplacer le mail d'origine vers les Éléments supprimés après rangement. */
  deleteAfterFiling: boolean;
  /** Appliquer la catégorie « Rangé » (verte) au mail d'origine. */
  autoCategorize: boolean;
  /**
   * Appliquer AUSSI la catégorie du numéro d'affaire (« 19048 ») du projet de
   * destination. Indépendante d'`autoCategorize`, et sans effet quand le projet
   * n'a pas de numéro à cinq chiffres (espace d'équipe, intitulé interne).
   */
  categorizeProjectNumber: boolean;
  /** Proposer « Envoyer et ranger » sur les brouillons. */
  sendAndFile: boolean;
}

export async function getPreferences(): Promise<UserPreferences> {
  return getApiClient().get('outlook-preferences').json<UserPreferences>();
}

/**
 * Écriture PARTIELLE : seules les clés fournies sont modifiées. Chaque
 * interrupteur enregistre séparément, donc deux fenêtres ouvertes sur le même
 * compte ne s'écrasent pas mutuellement.
 */
export async function setPreferences(
  patch: Partial<UserPreferences>,
): Promise<UserPreferences> {
  return getApiClient()
    .post('outlook-preferences', { json: patch })
    .json<UserPreferences>();
}

export async function adminListUsers(): Promise<AdminUser[]> {
  return getApiClient().get('outlook-admin-list-users').json<AdminUser[]>();
}

export async function adminSetUserRole(input: {
  oid: string;
  isAdmin: boolean;
}): Promise<void> {
  await getApiClient().post('outlook-admin-set-user-role', { json: input });
}

export async function adminMissingGroups(): Promise<MissingGroupProject[]> {
  return getApiClient()
    .get('outlook-admin-missing-groups')
    .json<MissingGroupProject[]>();
}

/**
 * Espaces collaboratifs du groupe Microsoft 365 lié à un espace.
 *
 * `teamsStatus` distingue « pas d'équipe » de « impossible de savoir » : afficher
 * un lien Teams qui n'existe pas mène l'utilisateur dans une erreur Teams, et
 * confondre les deux cas rendrait le problème indiagnostiquable.
 */
export interface GroupInfo {
  groupId: string;
  sharepointUrl: string;
  siteName: string | null;
  /** Renseigné seulement quand `teamsStatus === 'ok'`. */
  teamsUrl: string | null;
  /**
   * `none` = groupe jamais « teamifié » (cas courant) ; `unavailable` = équipe
   * existante mais Graph a refusé de la décrire, ce qui signale en général
   * l'autorisation applicative `Team.ReadBasic.All` absente du consentement.
   */
  teamsStatus: 'ok' | 'none' | 'unavailable';
}
export async function groupInfo(m365GroupId: string): Promise<GroupInfo> {
  return getApiClient()
    .get('outlook-group-info', { searchParams: { groupId: m365GroupId } })
    .json<GroupInfo>();
}

/**
 * Espace ACC (Autodesk Construction Cloud) rapproché d'un projet par son NUMÉRO
 * D'AFFAIRE — rien ne relie en base un projet de l'add-in à un projet ACC.
 *
 * SIX ÉTATS, et aucun n'est décoratif : le rapprochement peut aboutir, viser un
 * espace archivé, ne rien trouver, ne pas être tentable, être ambigu, ou avoir
 * échoué. Les réduire à « lien / pas de lien » ferait passer un compte Autodesk
 * injoignable pour un projet sans espace ACC.
 */
export interface AccProjectInfo {
  /**
   * `ok` un seul espace actif · `archived` aucun actif mais un archivé (`url`
   * renseigné, à annoncer comme tel) · `ambiguous` plusieurs actifs portent ce
   * numéro · `none` aucun espace ACC · `no-number` le projet n'a pas de numéro à
   * cinq chiffres (espace d'équipe, ou intitulé interne dans `number`) ·
   * `unavailable` l'API Autodesk n'a pas répondu.
   */
  status: 'ok' | 'archived' | 'ambiguous' | 'none' | 'no-number' | 'unavailable';
  /** Numéro cherché ; `null` quand `status === 'no-number'`. */
  number: string | null;
  projectId?: string | null;
  projectName?: string | null;
  /** Renseigné pour `ok` et `archived` seulement. */
  url?: string | null;
}
export async function accProject(containerId: string): Promise<AccProjectInfo> {
  return getApiClient()
    .get('outlook-acc-project', { searchParams: { containerId } })
    .json<AccProjectInfo>();
}

export interface TrashFolder {
  id: string;
  name: string;
  container_id: string;
  parent_id: string | null;
  deleted_at: string;
  deleted_by: string | null;
}
export interface TrashMail {
  id: string;
  subject: string;
  container_id: string;
  folder_id: string;
  deleted_at: string;
  deleted_by: string | null;
  received_at: string | null;
}
export interface TrashResponse {
  retentionDays: number;
  folders: TrashFolder[];
  mails: TrashMail[];
}
export async function adminListTrash(): Promise<TrashResponse> {
  return getApiClient().get('outlook-admin-list-trash').json<TrashResponse>();
}
export async function adminRestore(input: {
  kind: 'folder' | 'mail';
  id: string;
}): Promise<void> {
  await getApiClient().post('outlook-admin-restore', { json: input });
}

// Synchronisations : dernière exécution par source (onglet Administration).
export type SyncSource = 'prism' | 'autolink_groups' | 'aad_members';
export interface SyncRunStatus {
  source: SyncSource;
  status: 'success' | 'error' | 'running';
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  itemsTotal: number | null;
  itemsOk: number | null;
  itemsSkipped: number | null;
  detail: Record<string, unknown> | null;
  error: string | null;
}
export async function adminSyncRuns(): Promise<SyncRunStatus[]> {
  return getApiClient().get('outlook-admin-sync-runs').json<SyncRunStatus[]>();
}

export interface BackfillAttachmentNamesResult {
  processed: number;
  updated: number;
  failed: number;
  /** Détail des échecs — une passe silencieuse sur ses ratés serait trompeuse. */
  failures: { id: string; reason: string }[];
  /** Ce qu'il reste APRÈS ce lot, échecs de ce passage compris. */
  remaining: number | null;
  done: boolean;
}

/**
 * Renseigne `attachment_names` sur les mails rangés avant la colonne 0024.
 * Traite un lot et rend la main : à rappeler jusqu'à `done`.
 *
 * Timeout allongé : chaque mail coûte une lecture S3 plus un appel au
 * convertisseur, là où 30 s suffisent à un appel ordinaire.
 */
export async function backfillAttachmentNames(
  limit?: number,
): Promise<BackfillAttachmentNamesResult> {
  return getApiClient()
    .post('outlook-admin-backfill-attachment-names', {
      json: limit === undefined ? {} : { limit },
      timeout: 120_000,
    })
    .json<BackfillAttachmentNamesResult>();
}

// ---------------------------------------------------------------------------
// Inventaire des espaces (onglet d'administration « Tous les espaces »)
//
// À NE PAS CONFONDRE avec `listContainers()`, qui sert l'arbre : celui-là filtre
// par accessibilité et masque les espaces archivés. L'inventaire, lui, montre
// TOUT — c'est ce qui permet de récupérer un espace dont la visibilité a été
// verrouillée par erreur. D'où la garde `requireAdmin` côté serveur.
// ---------------------------------------------------------------------------

/** Tris acceptés par le serveur. Toute autre valeur est rejetée en 400. */
export type AdminSpaceSort = 'name' | 'size' | 'mails';

export interface AdminSpaceRow {
  id: string;
  kind: ContainerKind;
  name: string;
  /** Projets seulement. */
  number: string | null;
  /** Projets seulement (`actif`, `standby`, `termine`, `annule`). */
  status: string | null;
  archived: boolean;
  isRestricted: boolean;
  /**
   * 0029 : `false` = espace INVISIBLE pour les utilisateurs (absent des listes et
   * de la recherche, rangement refusé). Cet écran est le seul endroit où l'écart
   * entre les projets PRISM et les groupes Microsoft 365 apparaît.
   */
  hasGroup: boolean;
  m365GroupId: string | null;
  folderCount: number;
  mailCount: number;
  /** Volume S3 des mails vivants, en octets. */
  bytes: number;
  /** Mails en corbeille : ils occupent ENCORE S3 jusqu'à la purge. */
  trashedMailCount: number;
  trashedBytes: number;
  /** Droits nominatifs posés sur l'espace. */
  userCount: number;
  /** Groupes Entra accordés. */
  groupCount: number;
  /**
   * Effectif réel dédoublonné : personnes nommées + membres des groupes accordés.
   * N'inclut PAS les administrateurs de l'add-in, qui voient tout espace de toute
   * façon. Sans signification quand l'espace est public.
   */
  peopleCount: number;
}

export interface AdminSpacesTotals {
  containers: number;
  restricted: number;
  archived: number;
  /** 0029 : espaces qu'aucun utilisateur ne voit plus. La mesure de l'écart. */
  withoutGroup: number;
  folders: number;
  mails: number;
  bytes: number;
  trashedMails: number;
  trashedBytes: number;
}

export interface AdminSpacesResponse {
  /** Espaces retenus par le FILTRE, pas seulement ceux de la page. */
  total: number;
  limit: number;
  offset: number;
  sort: AdminSpaceSort;
  /** Cumuls sur tout le périmètre filtré — jamais sur la page seule. */
  totals: AdminSpacesTotals;
  rows: AdminSpaceRow[];
}

export interface AdminSpacesQuery {
  /** Cherche dans le nom (accents ignorés) ou le numéro de projet. */
  q?: string;
  restrictedOnly?: boolean;
  /** Défaut serveur : true. Les archivés occupent du stockage, on les compte. */
  includeArchived?: boolean;
  /**
   * 0029, trois états : `undefined` = tout l'inventaire ; `true` = seulement les
   * espaces sans groupe Microsoft 365 (la liste de travail de la correction) ;
   * `false` = seulement ceux qui en ont un.
   */
  missingGroup?: boolean;
  sort?: AdminSpaceSort;
  limit?: number;
  offset?: number;
}

export async function adminListSpaces(input: AdminSpacesQuery = {}): Promise<AdminSpacesResponse> {
  const searchParams: Record<string, string> = {};
  const q = input.q?.trim();
  if (q) searchParams.q = q;
  if (input.restrictedOnly) searchParams.restricted_only = 'true';
  if (input.includeArchived === false) searchParams.include_archived = 'false';
  // `!= null` et non un test de vérité : `missingGroup: false` est un filtre
  // demandé (« seulement ceux qui ont un groupe »), pas une absence de filtre.
  if (input.missingGroup != null) searchParams.missing_group = String(input.missingGroup);
  if (input.sort) searchParams.sort = input.sort;
  if (input.limit != null) searchParams.limit = String(input.limit);
  if (input.offset != null) searchParams.offset = String(input.offset);
  return getApiClient()
    .get('outlook-admin-list-spaces', { searchParams })
    .json<AdminSpacesResponse>();
}

// ---------------------------------------------------------------------------
// Visibilité d'un espace — popup « Gérer la visibilité »
//
// Les cinq endpoints ci-dessous se consomment ENSEMBLE (on choisit une personne
// ou un groupe, on inspecte les membres du groupe, on relit l'état courant, on
// enregistre). Ils partagent donc un vocabulaire strict, garanti côté serveur :
//   • une personne est désignée par `email` (jamais `upn`, jamais un id) ;
//   • un groupe est désigné par `groupId` (jamais `id`) ;
//   • `fonction` / `photoUrl` sont toujours présents, à `null` si inconnus.
// ---------------------------------------------------------------------------

/** Rôle d'un droit — reflète le CHECK de `outlook.container_permissions.role`. */
export type ContainerRole = 'reader' | 'contributor' | 'admin';

/**
 * Une personne de l'annuaire PRISM.
 *
 * `email` est la CLÉ, pas un simple libellé : c'est lui qu'on renvoie ensuite
 * dans `principalUpn`, et c'est lui qui sert de `key` de liste React. Le serveur
 * n'expose délibérément AUCUN id de profil — un uuid PRISM glissé dans
 * `principalId` produirait un droit accepté mais sans effet (cf. l'entête de
 * `outlook-list-profiles`).
 */
export interface ProfileSummary {
  email: string;
  displayName: string;
  fonction: string | null;
  photoUrl: string | null;
}

/** Annuaire des collaborateurs actifs. `q` filtre sur le nom ou l'e-mail (accents ignorés). */
export async function listProfiles(q?: string): Promise<ProfileSummary[]> {
  const term = q?.trim();
  return getApiClient()
    .get('outlook-list-profiles', term ? { searchParams: { q: term } } : undefined)
    .json<ProfileSummary[]>();
}

export interface ContainerUserPrincipal {
  type: 'user';
  /** `null` sur un droit legacy posé par OID Entra plutôt que par e-mail. */
  email: string | null;
  /** OID Entra d'un droit legacy. À RENVOYER TEL QUEL au `set`, qui réécrit toute la liste. */
  oid: string | null;
  displayName: string | null;
  fonction: string | null;
  photoUrl: string | null;
  role: ContainerRole;
  /** false = droit posé sur quelqu'un d'absent de l'annuaire (départ, prestataire). */
  knownInPrism: boolean;
}

export interface ContainerGroupPrincipal {
  type: 'aad_group';
  groupId: string;
  /** `null` si le libellé n'est ni en cache ni joignable via Graph. */
  displayName: string | null;
  /** Effectif du cache local, pas un appel Graph à chaud. */
  memberCount: number;
  role: ContainerRole;
}

export type ContainerPrincipal = ContainerUserPrincipal | ContainerGroupPrincipal;

export interface ContainerPermissions {
  containerId: string;
  isRestricted: boolean;
  /** Uniquement les droits `source = 'manual'` : les seuls que la popup pilote. */
  principals: ContainerPrincipal[];
}

/** État courant des droits d'un espace — à appeler AVANT tout `setContainerRestriction`. */
export async function getContainerPermissions(containerId: string): Promise<ContainerPermissions> {
  return getApiClient()
    .get('outlook-get-container-permissions', { searchParams: { container_id: containerId } })
    .json<ContainerPermissions>();
}

export interface AadGroupSummary {
  groupId: string;
  displayName: string | null;
  /** Sert à départager deux groupes homonymes ; `groupId` ne doit jamais s'afficher. */
  mail: string | null;
}

/** Groupes Entra du tenant, filtrables par préfixe de nom. */
export async function listAadGroups(q?: string): Promise<AadGroupSummary[]> {
  const term = q?.trim();
  return getApiClient()
    .get('outlook-list-aad-groups', term ? { searchParams: { q: term } } : undefined)
    .json<AadGroupSummary[]>();
}

export interface AadGroupMember {
  /** `null` pour un principal Entra sans UPN ni mail exploitable (rare). */
  email: string | null;
  displayName: string;
  fonction: string | null;
  photoUrl: string | null;
  knownInPrism: boolean;
}

export interface AadGroupMembers {
  groupId: string;
  displayName: string;
  /**
   * Fraîcheur des APPARTENANCES (`max(synced_at)` des membres), pas celle du
   * libellé du groupe. `null` = groupe jamais matérialisé.
   */
  syncedAt: string | null;
  /** true = liste servie depuis le cache, la resynchronisation Entra a échoué. */
  stale: boolean;
  /** Présent seulement si `stale` : message déjà assaini, affichable tel quel. */
  syncError?: string;
  members: AadGroupMember[];
}

/** Composition nominative d'un groupe Entra. Déclenche une resynchronisation best-effort. */
export async function aadGroupMembers(groupId: string): Promise<AadGroupMembers> {
  return getApiClient()
    .get('outlook-aad-group-members', { searchParams: { group_id: groupId } })
    .json<AadGroupMembers>();
}

/**
 * Un ayant droit à écrire.
 *
 * Pour un utilisateur : `principalUpn` (son e-mail) est le cas NOMINAL ;
 * `principalId` n'est là que pour redonner un droit legacy relu tel quel via
 * `getContainerPermissions().principals[].oid`. Ne jamais y mettre autre chose
 * qu'un OID Entra. Pour un groupe : `principalId` est obligatoire.
 */
export interface SetRestrictionPrincipal {
  principalType: 'user' | 'aad_group';
  principalId?: string | null;
  principalUpn?: string | null;
  /** Défaut serveur : 'contributor'. */
  role?: ContainerRole;
}

export interface SetRestrictionInput {
  containerId: string;
  isRestricted: boolean;
  /**
   * LISTE COMPLÈTE, pas un delta : le serveur efface les droits manuels
   * existants puis réinsère celle-ci. Tout ce qui n'y figure pas est SUPPRIMÉ.
   */
  permissions: SetRestrictionPrincipal[];
}

/** Résultat de la resynchronisation immédiate d'un groupe accordé. */
export interface GroupSyncOutcome {
  groupId: string;
  ok: boolean;
  memberCount: number;
  error?: string;
}

export interface SetRestrictionResult {
  status: 'ok';
  /**
   * Peut différer de l'`isRestricted` demandé : « restreint » avec zéro
   * destinataire est ramené à « public » par le serveur (sinon l'espace
   * deviendrait invisible de tous, sans recours).
   */
  isRestricted: boolean;
  principalCount: number;
  /**
   * Vide si aucun groupe. Un `ok: false` signifie que le droit est bien
   * enregistré mais que ses membres ne seront pris en compte qu'au prochain
   * passage du cron — à signaler à l'utilisateur.
   */
  groupSync: GroupSyncOutcome[];
}

/** Enregistre la visibilité d'un espace et la liste COMPLÈTE de ses ayants droit. */
export async function setContainerRestriction(
  input: SetRestrictionInput,
): Promise<SetRestrictionResult> {
  return getApiClient()
    .post('outlook-set-container-restriction', { json: input })
    .json<SetRestrictionResult>();
}
